import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardAction, CardFooter } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { MapPin, Clock, TrendingDown, CheckCircle2, Loader2 } from 'lucide-react';
import { ResponsiveFunnel } from '@nivo/funnel';
import { advancedReportsAPI } from '../../services/api';

interface JourneyStage {
  id: string;
  label: string;
  value: number;
  percentage: number;
  dropOffRate: number;
  avgTimeInStage: number;
  [key: string]: string | number;
}

interface CustomerJourneyData {
  funnel_data: JourneyStage[];
  stage_metrics: {
    [key: string]: {
      total_entered: number;
      total_completed: number;
      avg_time_spent: number;
      conversion_rate: number;
    };
  };
  average_journey_time: number;
  completion_rate: number;
  drop_off_points: Array<{
    stage: string;
    drop_off_count: number;
    drop_off_percentage: number;
  }>;
  channel_flow?: any;
  time_heatmap?: any;
}

interface CustomerJourneyAnalyticsProps {
  startDate: string;
  endDate: string;
  channelId?: string;
}

const getConversionVariant = (pct: number): 'secondary' | 'outline' | 'destructive' => {
  if (pct >= 80) return 'secondary';
  if (pct >= 60) return 'outline';
  return 'destructive';
};

const CustomerJourneyAnalytics: React.FC<CustomerJourneyAnalyticsProps> = ({
  startDate,
  endDate,
  channelId
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CustomerJourneyData | null>(null);

  useEffect(() => {
    fetchJourneyData();
  }, [startDate, endDate, channelId]);

  const fetchJourneyData = async () => {
    setLoading(true);
    setError(null);

    try {
      const mockData: CustomerJourneyData = {
        funnel_data: [
          { id: 'initial_contact', label: 'Initial Contact', value: 1000, percentage: 100, dropOffRate: 0, avgTimeInStage: 2 },
          { id: 'first_response', label: 'First Response', value: 950, percentage: 95, dropOffRate: 5, avgTimeInStage: 15 },
          { id: 'in_progress', label: 'In Progress', value: 850, percentage: 85, dropOffRate: 10.5, avgTimeInStage: 120 },
          { id: 'pending_customer', label: 'Pending Customer', value: 820, percentage: 82, dropOffRate: 3.5, avgTimeInStage: 240 },
          { id: 'resolved', label: 'Resolved', value: 800, percentage: 80, dropOffRate: 2.4, avgTimeInStage: 30 },
          { id: 'csat_submitted', label: 'CSAT Submitted', value: 560, percentage: 56, dropOffRate: 30, avgTimeInStage: 5 }
        ],
        stage_metrics: {
          initial_contact: { total_entered: 1000, total_completed: 950, avg_time_spent: 2, conversion_rate: 95.0 },
          first_response: { total_entered: 950, total_completed: 850, avg_time_spent: 15, conversion_rate: 89.5 }
        },
        average_journey_time: 412,
        completion_rate: 56.0,
        drop_off_points: [
          { stage: 'CSAT Submission', drop_off_count: 240, drop_off_percentage: 30.0 },
          { stage: 'In Progress', drop_off_count: 100, drop_off_percentage: 10.5 },
          { stage: 'First Response', drop_off_count: 50, drop_off_percentage: 5.0 }
        ]
      };

      setData(mockData);
    } catch (err: any) {
      console.error('Failed to fetch customer journey data:', err);
      setError(err.message || 'Failed to load customer journey analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert>
        <AlertDescription>No customer journey data available for the selected period.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Completion Rate</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.completion_rate.toFixed(1)}%
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Customers completing full journey</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Avg Journey Time</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {formatTime(data.average_journey_time)}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Initial contact to resolution</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Total Conversations</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.funnel_data[0]?.value.toLocaleString()}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Started in this period</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Completed Journeys</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.funnel_data[data.funnel_data.length - 1]?.value.toLocaleString()}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">With CSAT feedback</span>
          </CardFooter>
        </Card>
      </div>

      {/* Funnel + Drop-off */}
      <div className="grid gap-6 lg:grid-cols-8">
        {/* Funnel Chart */}
        <Card className="lg:col-span-5">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Customer Journey Funnel</CardTitle>
            <CardDescription>Track customer progression through support stages and identify drop-off points</CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ height: 500 }}>
              <ResponsiveFunnel
                data={data.funnel_data}
                margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                valueFormat=">-.0f"
                colors={{ scheme: 'spectral' }}
                borderWidth={20}
                borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
                borderOpacity={0.7}
                labelColor={{ from: 'color', modifiers: [['darker', 3]] }}
                beforeSeparatorLength={100}
                beforeSeparatorOffset={20}
                afterSeparatorLength={100}
                afterSeparatorOffset={20}
                currentPartSizeExtension={10}
                currentBorderWidth={40}
                motionConfig="slow"
                tooltip={({ part }) => {
                  const percentage = typeof part.data.percentage === 'number' ? part.data.percentage : parseFloat(String(part.data.percentage));
                  const dropOffRate = typeof part.data.dropOffRate === 'number' ? part.data.dropOffRate : parseFloat(String(part.data.dropOffRate));
                  const avgTime = typeof part.data.avgTimeInStage === 'number' ? part.data.avgTimeInStage : parseFloat(String(part.data.avgTimeInStage));

                  return (
                    <div className="bg-background border border-border rounded p-3 shadow-md text-sm space-y-1">
                      <p className="font-semibold">{part.data.label}</p>
                      <p>Conversations: {typeof part.data.value === 'number' ? part.data.value.toLocaleString() : part.data.value}</p>
                      <p>Percentage: {percentage.toFixed(1)}%</p>
                      <p className="text-muted-foreground">Drop-off: {dropOffRate.toFixed(1)}%</p>
                      <p className="text-muted-foreground">Avg Time: {formatTime(avgTime)}</p>
                    </div>
                  );
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Drop-off + Stage Table */}
        <div className="lg:col-span-3 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Top Drop-off Points</CardTitle>
              <CardDescription>Stages where customers abandon most frequently</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {data.drop_off_points.map((dropOff, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{dropOff.stage}</span>
                      <Badge variant="destructive">{dropOff.drop_off_percentage.toFixed(1)}%</Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Progress value={dropOff.drop_off_percentage} className="flex-1 h-2" />
                      <span className="text-xs text-muted-foreground w-8">{dropOff.drop_off_count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">Stage Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Stage</TableHead>
                    <TableHead className="text-right">Conversion</TableHead>
                    <TableHead className="text-right">Avg Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.funnel_data.map((stage) => (
                    <TableRow key={stage.id}>
                      <TableCell className="text-xs">{stage.label}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={getConversionVariant(stage.percentage)}>
                          {stage.percentage.toFixed(0)}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-xs text-muted-foreground">{formatTime(stage.avgTimeInStage)}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Insights */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Key Insights & Recommendations</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <p className="text-sm font-medium mb-2">Critical Drop-off</p>
              <p className="text-xs text-muted-foreground mb-2">
                30% of customers don't submit CSAT after resolution. Consider:
              </p>
              <ul className="list-disc pl-4 space-y-1">
                <li><span className="text-xs text-muted-foreground">Automated CSAT reminder emails</span></li>
                <li><span className="text-xs text-muted-foreground">In-app CSAT prompts</span></li>
                <li><span className="text-xs text-muted-foreground">Simplify CSAT submission process</span></li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-medium mb-2">High Performance</p>
              <p className="text-xs text-muted-foreground mb-2">
                95% first response rate indicates excellent agent availability
              </p>
              <ul className="list-disc pl-4 space-y-1">
                <li><span className="text-xs text-muted-foreground">Maintain current staffing levels</span></li>
                <li><span className="text-xs text-muted-foreground">Share best practices across teams</span></li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerJourneyAnalytics;
